/* OBFUSCATED CODE - INTELLECTUAL PROPERTY PROTECTED */
export function sprayAt(x, y, { state, canvas, notifications }) {
  const ctx = canvas.activeCtx;
  const config = state.config;
  const cansData = state.cansData;
  const dripMap = state.dripMap;
  const paintedPixels = state.paintedPixels;
  const presetConfig = state.presetConfig || {};
  const enableDrips = state.enableDrips;
  const paintConsumption = state.paintConsumption || 1;
  const activeCan = cansData.find(c => c.id === config.activeCanId);
  if (!activeCan || activeCan.paintLeft <= 0) {
    notifications.showPaintEmptyToast(config.activeCanId);
    return;
  }
  const now = performance.now();
  let speed = 0;
  if (state.lastSprayPos && state.lastSprayTime !== null) {
    const dt = now - state.lastSprayTime;
    const dist = Math.hypot(x - state.lastSprayPos.x, y - state.lastSprayPos.y);
    speed = Math.min(1, dist / (dt || 1) / config.speedFactor);
  }
  const scale = config.lineScale;
  const minDot = 0.7 * scale;
  const maxDot = 1 * scale;
  const dotFromSpeed = maxDot - (maxDot - minDot) * speed;
  const minRadius = config.sprayRadius * 0.7 * scale;
  const maxRadius = config.sprayRadius * 5 * scale;
  const radiusFromSpeed = minRadius + (maxRadius - minRadius) * speed * 0.7;
  const minAlpha = 0.15;
  const maxAlpha = 0.55;
  const alphaFromSpeed = maxAlpha - (maxAlpha - minAlpha) * speed;
  const isFlatMode = state.flatMode === true;
  const speedReduction = isFlatMode ? (1 - speed * 0.65) : 1;
  const maxDotsToDraw = Math.max(200, Math.floor(Math.min(config.dotsPerTick, 7000) * speedReduction));
  function checkStaticSprayLocal(xp, yp) {
    if (!state.lastStaticSprayPos) {
      state.lastStaticSprayPos = { x: xp, y: yp };
      state.staticSprayStartTime = Date.now();
      state.isStaticSpraying = true;
      return false;
    }
    const distance = Math.hypot(xp - state.lastStaticSprayPos.x, yp - state.lastStaticSprayPos.y);
    const threshold = 5;
    if (distance > threshold) {
      state.lastStaticSprayPos = { x: xp, y: yp };
      state.staticSprayStartTime = Date.now();
      state.isStaticSpraying = false;
      return false;
    }
    const timeHeld = Date.now() - state.staticSprayStartTime;
    if (timeHeld > 100) {
      state.isStaticSpraying = true;
      return timeHeld;
    }
    state.isStaticSpraying = false;
    return false;
  }
  const timeHeld = checkStaticSprayLocal(x, y);
  let paintUsedCount = 0;
  const hasLastPos = !!state.lastSprayPos && speed > 0.05;
  const lx = hasLastPos ? state.lastSprayPos.x : x;
  const ly = hasLastPos ? state.lastSprayPos.y : y;
  ctx.fillStyle = config.currentColor;
  ctx.globalAlpha = alphaFromSpeed * 0.95;
  let hasArc = false;
  ctx.beginPath();
  for (let i = 0; i < maxDotsToDraw; i++) {
    const t = hasLastPos ? Math.random() : 0;
    const cx = lx + (x - lx) * t;
    const cy = ly + (y - ly) * t;
    const angle = Math.random() * 2 * Math.PI;
    const r = isFlatMode
      ? ((Math.random() + Math.random()) / 2) * radiusFromSpeed
      : Math.random() * radiusFromSpeed;
    const dx = Math.cos(angle) * r;
    const dy = Math.sin(angle) * r;
    const size = minDot + Math.random() * (maxDot - minDot);
    if (size <= 2.5) {
      ctx.fillRect(cx + dx - size, cy + dy - size, size * 2, size * 2);
    } else {
      ctx.moveTo(cx + dx + size, cy + dy);
      ctx.arc(cx + dx, cy + dy, size, 0, 2 * Math.PI);
      hasArc = true;
    }
    if (timeHeld && enableDrips) {
      const cellX = Math.round(cx + dx);
      const cellY = Math.round(cy + dy);
      const cellKey = (cellX << 12) | (cellY & 0xFFF);
      dripMap[cellKey] = (dripMap[cellKey] || 0) + 1;
      const drops = dripMap[cellKey];
      const threshold = Math.max(1, 7 * scale);
      if (drops > threshold && drops % 1 === 0 && state.isStaticSpraying) {
        const dripLen = Math.min(
          3 * scale * (.1 + timeHeld / .7),
          Math.sqrt(drops - threshold) * .9 * scale + Math.random() * 7
        );
        ctx.save();
        ctx.globalAlpha = 0.12 + Math.random() * 0.7;
        ctx.strokeStyle = config.currentColor;
        const dripWidth = (presetConfig && presetConfig.dripWidth) || 0.1;
        const dripWidthVariation = (presetConfig && presetConfig.dripWidthVariation) || .1;
        ctx.lineWidth = dripWidth + Math.random() * dripWidthVariation;
        ctx.beginPath();
        ctx.moveTo(cellX + (Math.random() * 7), cellY + size / 4);
        ctx.lineTo(cellX + (Math.random() * 2 - 1), cellY + size / 7 + dripLen);
        ctx.stroke();
        ctx.restore();
      }
    }
    if (i % 2 === 0) {
      const px = Math.round(cx + dx);
      const py = Math.round(cy + dy);
      const key = (px << 12) | (py & 0xFFF);
      if (!paintedPixels.has(key)) {
        paintedPixels.add(key);
        paintUsedCount += 2;
      }
    }
  }
  if (hasArc) {
    ctx.fill();
  }
  if (paintUsedCount > 0) {
    const canvasEl = canvas.canvas;
    const totalPixels = (canvasEl && canvasEl.width && canvasEl.height) ? (canvasEl.width * canvasEl.height) : 1;
    const pixelsPerCan = state.pixelsPerCan || 960000;
    const paintUnitsPerPixel = state.config.paintMax / pixelsPerCan;
    const totalPaintUsed = Math.max(1, Math.round(paintUsedCount * paintUnitsPerPixel * (state.paintConsumption || 1)));
    state.updateCanPaint(state.config.activeCanId, totalPaintUsed);
  }
  state.lastSprayPos = { x, y };
  state.lastSprayTime = now;
  ctx.globalAlpha = 1;
}